<!--
  ZoneTable part — the FIELD LIST behind the Filters trigger: one labelled control per filter, each
  with its own Clear, plus the `#filters` slot for anything the declarative model cannot express.
  INTERNAL: not a Zone component, no story, not in llms.txt.

  This exists so the two surfaces — the side drawer and the anchored floating menu — render the
  SAME fields from ONE definition. They used to be spelled out under each surface, with a spec
  asserting the copies agreed; that guard is still there, but there is nothing left to drift.

  It reads values out of the `values` bag and asks for changes through `set(key, values)`. Every
  control writes through that single event, which is what keeps `filterValues` uniformly
  `key -> value[]` whatever produced it — a `select` holds one element, a `toggle` holds `[true]`.

  A filter declared WITHOUT `options` (and not a `toggle`) gets no control here: there is nothing to
  list, so it is the caller's to draw in `#filters` — the declaration exists for its predicate, for
  Reset and for the badge count. `options: []` is different: that is a list that is merely empty
  (server options still loading) and renders as usual.
-->

<template>
  <!-- ONE root: each surface passes its own layout classes (the drawer wants plain stacking,
       the popover wants a scrolling, padded box), and `class` only falls through to a single
       root element. -->
  <div data-testid="table-filters-fields">
    <div
      v-for="filter in controlledFilters"
      :key="filter.key"
      :data-testid="`filter-${filter.key}`"
    >
      <div class="mb-1 flex items-center justify-between gap-2">
        <span class="truncate text-sm font-medium text-gray-700">{{ filter.label }}</span>
        <button
          v-if="isFilterActive(filter)"
          type="button"
          class="shrink-0 text-xs text-primary-600 hover:text-primary-700 focus-visible:outline-none focus-visible:underline"
          :data-testid="`filter-clear-${filter.key}`"
          @click="$emit('set', filter.key, [])"
        >
          {{ t('clear') }}
        </button>
      </div>

      <ZoneCheckbox
        v-if="filter.type === 'toggle'"
        :model-value="toggleFilterValue(filter)"
        @update:model-value="onToggle(filter, $event)"
      />
      <!-- Base (40px), not the 32px sm: these are the form controls of a filter surface,
           not toolbar chrome. ZoneSelect calls it `md`; Figma calls it Base. -->
      <ZoneSelect
        v-else-if="filter.type === 'select'"
        searchable
        size="md"
        :options="filter.options || []"
        :model-value="singleFilterValue(filter)"
        @update:model-value="onSingle(filter, $event)"
      />
      <ZoneSelect
        v-else
        multiple
        searchable
        size="md"
        :options="filter.options || []"
        :model-value="filterValue(filter)"
        :resource="{ singular: filter.label, plural: filter.label }"
        @update:model-value="$emit('set', filter.key, $event)"
      />
    </div>

    <slot
      name="filters"
      :values="values"
      :set-filter="setFilter"
      :reset="reset"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneCheckbox from '../Checkbox.vue';
import ZoneSelect from '../Select.vue';
import * as filterHelpers from './filters';
import type { TableFilter, TableFilterValue } from './types';

defineOptions({ name: 'ZoneTableFilterFields' });

const { t } = useI18n();

const props = defineProps({
  /** The non-inline filters, in declaration order. */
  filters: {
    type: Array as PropType<TableFilter[]>,
    required: true,
  },
  values: {
    type: Object as PropType<Record<string, TableFilterValue[]>>,
    required: true,
  },
  /** Handed straight to the `#filters` slot, so a caller's control writes like any other. */
  setFilter: {
    type: Function as PropType<(key: string, values: unknown) => void>,
    required: true,
  },
  reset: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const emit = defineEmits(['set']);

// See the header: no `options` means the caller draws the control in `#filters`.
const controlledFilters = computed<TableFilter[]>(() => props.filters.filter((filter) => filter.type === 'toggle' || filter.options !== undefined));

function filterValue(filter: TableFilter): TableFilterValue[] {
  return filterHelpers.filterValue(props.values, filter);
}

function singleFilterValue(filter: TableFilter): TableFilterValue | null {
  return filterHelpers.singleFilterValue(props.values, filter);
}

function toggleFilterValue(filter: TableFilter): boolean {
  return filterHelpers.toggleFilterValue(props.values, filter);
}

function isFilterActive(filter: TableFilter): boolean {
  return filterHelpers.isFilterActive(props.values, filter);
}

function onSingle(filter: TableFilter, value: unknown) {
  emit('set', filter.key, filterHelpers.asSingleValue(value));
}

function onToggle(filter: TableFilter, checked: boolean) {
  emit('set', filter.key, filterHelpers.asToggleValue(checked));
}
</script>

<i18n lang="json">
{
  "en": {
    "clear": "Clear"
  }
}
</i18n>
